import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Checkbox,
  FormControl,
  CircularProgress,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { BottomArrowIcon, SearchIcon, CrossIcon, CheckIcon, FileIcon } from "../Icons";
import {
  IconContainer,
  Label,
  StyledSelect,
  ValueContainer,
  ChipsContainer,
  StyledChip,
  SearchContainer,
  menuPaperStyles,
} from "../Select/Select.styled";
import { Search } from "../Search";
import {
  SectionHeader,
  SectionHeaderText,
  ClearButton,
  ClearButtonText,
  CustomProcedureItem,
  CdtCodeItem,
  ItemLabel,
  EmptyMessage,
} from "./CDTAndCustomProcedureSelect.styled";
import { colors } from "../../theme/colors";
import { CustomProcedure, organizationService } from "../../services/organizationService";

interface InternalCdtCode {
  code: string;
  description: string;
  fee: number;
  durationMinutes: number;
}

export interface CDTAndCustomProcedureValue {
  cdtCodes: string[];
  customProcedureId?: number;
}

interface CDTAndCustomProcedureSelectProps {
  label?: string;
  placeholder?: string;
  value?: CDTAndCustomProcedureValue;
  onChange?: (value: CDTAndCustomProcedureValue, allCdtCodes: string[], allDescriptions: string[], totalFee: number, totalDurationMinutes: number) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  fullWidth?: boolean;
  width?: string | number;
  name?: string;
  id?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
}

const MAX_CDT_CODES = 4;

const CDTAndCustomProcedureSelect: React.FC<CDTAndCustomProcedureSelectProps> = ({
  label,
  placeholder,
  value,
  onChange,
  onFocus,
  onBlur,
  disabled = false,
  fullWidth = false,
  width,
  name,
  id,
  error = false,
  helperText,
  required = false,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [customProcedures, setCustomProcedures] = useState<CustomProcedure[]>([]);
  const [internalCdtCodes, setInternalCdtCodes] = useState<InternalCdtCode[]>([]);
  const [proceduresLoading, setProceduresLoading] = useState(false);

  const currentValue = useMemo(
    () => value || { cdtCodes: [], customProcedureId: undefined },
    [value],
  );

  useEffect(() => {
    const fetchProcedures = async () => {
      try {
        setProceduresLoading(true);
        const categories = await organizationService.getProcedures();
        const allCustomProcedures: CustomProcedure[] = [];
        const allCdtCodes: InternalCdtCode[] = [];

        categories.forEach((category) => {
          category.procedures.forEach((proc) => {
            if (proc.customProcedureId) {
              // Custom procedure
              allCustomProcedures.push({
                id: proc.customProcedureId,
                cdtCodeId: proc.cdtCodeId,
                codes: proc.codes,
                name: proc.name,
                description: proc.description,
                fee: proc.fee,
                durationMinutes: proc.durationMinutes,
              });
            } else {
              // Regular CDT code
              if (proc.codes[0]) {
                allCdtCodes.push({
                  code: proc.codes[0],
                  description: proc.description ?? "",
                  fee: proc.fee ?? 0,
                  durationMinutes: proc.durationMinutes ?? 0,
                });
              }
            }
          });
        });

        setCustomProcedures(allCustomProcedures);
        setInternalCdtCodes(allCdtCodes);
      } catch (error) {
        console.error("Error fetching procedures:", error);
      } finally {
        setProceduresLoading(false);
      }
    };

    if (open) {
      fetchProcedures();
    }
  }, [open]);

  const selectedCustomProcedure = useMemo(() => {
    if (!currentValue.customProcedureId) return null;
    return customProcedures.find((p) => p.id === currentValue.customProcedureId) || null;
  }, [currentValue.customProcedureId, customProcedures]);

  const allSelectedCdtCodes = useMemo(() => {
    const codes = new Set<string>(currentValue.cdtCodes);
    if (selectedCustomProcedure) {
      selectedCustomProcedure.codes.forEach((code) => codes.add(code));
    }
    return Array.from(codes);
  }, [currentValue.cdtCodes, selectedCustomProcedure]);

  const filteredCustomProcedures = useMemo(() => {
    const withCdtCodes = customProcedures.filter((proc) => proc.codes.length > 0);
    if (!searchQuery.trim()) return withCdtCodes;
    const query = searchQuery.toLowerCase();
    return withCdtCodes.filter(
      (proc) =>
        proc.name.toLowerCase().includes(query) ||
        proc.codes.some((code) => code.toLowerCase().includes(query)),
    );
  }, [customProcedures, searchQuery]);

  const filteredCdtCodes = useMemo(() => {
    if (!searchQuery.trim()) return internalCdtCodes.filter((c) => c.code);
    const query = searchQuery.toLowerCase();
    return internalCdtCodes.filter(
      (code) =>
        code.code &&
        ((code.code.toLowerCase().includes(query) ||
          (code.description ?? "").toLowerCase().includes(query))),
    );
  }, [internalCdtCodes, searchQuery]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchQuery("");
  };

  const getDescriptionsForCodes = useCallback(
    (codes: string[]): string[] => {
      const descMap = new Map<string, string>(
        internalCdtCodes.map((c) => [c.code, c.description]),
      );
      return codes.map((code) => descMap.get(code) ?? "");
    },
    [internalCdtCodes],
  );

  /**
   * Calculate the total fee and duration for the given selection.
   * - Custom procedure contributes its own fee/duration.
   * - Extra individually-selected CDT codes (not in the bundle) add their own.
   */
  const calculateTotals = useCallback(
    (extraCdtCodes: string[], customProc: CustomProcedure | null): { totalFee: number; totalDuration: number } => {
      let totalFee = customProc?.fee ?? 0;
      let totalDuration = customProc?.durationMinutes ?? 0;
      const bundleCodes = new Set(customProc?.codes ?? []);
      extraCdtCodes
        .filter((code) => !bundleCodes.has(code))
        .forEach((code) => {
          const found = internalCdtCodes.find((c) => c.code === code);
          totalFee += found?.fee ?? 0;
          totalDuration += found?.durationMinutes ?? 0;
        });
      return { totalFee, totalDuration };
    },
    [internalCdtCodes],
  );

  const handleRemoveChip = useCallback(
    (codeToRemove: string, event: React.MouseEvent) => {
      event.stopPropagation();
      const newCdtCodes = currentValue.cdtCodes.filter((code) => code !== codeToRemove);
      const bundleCodes = selectedCustomProcedure?.codes || [];
      const allCodes = Array.from(new Set([...newCdtCodes, ...bundleCodes]));
      const { totalFee, totalDuration } = calculateTotals(newCdtCodes, selectedCustomProcedure ?? null);
      onChange?.({ ...currentValue, cdtCodes: newCdtCodes }, allCodes, getDescriptionsForCodes(allCodes), totalFee, totalDuration);
    },
    [currentValue, onChange, selectedCustomProcedure, getDescriptionsForCodes, calculateTotals],
  );

  const handleRemoveCustomProcedure = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      // When removing the custom procedure, only individual CDT codes remain
      const { totalFee, totalDuration } = calculateTotals(currentValue.cdtCodes, null);
      onChange?.(
        { cdtCodes: currentValue.cdtCodes, customProcedureId: undefined },
        currentValue.cdtCodes,
        getDescriptionsForCodes(currentValue.cdtCodes),
        totalFee,
        totalDuration,
      );
    },
    [currentValue.cdtCodes, onChange, getDescriptionsForCodes, calculateTotals],
  );

  const handleCustomProcedureSelect = useCallback(
    (procedure: CustomProcedure) => {
      const procedureCodes = new Set(procedure.codes);
      const additionalCodes = currentValue.cdtCodes.filter(
        (code) => !procedureCodes.has(code),
      );

      if (procedureCodes.size + additionalCodes.length > MAX_CDT_CODES) {
        return;
      }

      const allCodes = Array.from(new Set([...additionalCodes, ...procedure.codes]));
      const { totalFee, totalDuration } = calculateTotals(additionalCodes, procedure);
      onChange?.({ cdtCodes: additionalCodes, customProcedureId: procedure.id }, allCodes, getDescriptionsForCodes(allCodes), totalFee, totalDuration);
    },
    [currentValue.cdtCodes, onChange, getDescriptionsForCodes, calculateTotals],
  );

  const handleCdtCodeSelect = useCallback(
    (code: string) => {
      const bundleCodes = selectedCustomProcedure?.codes || [];
      if (allSelectedCdtCodes.includes(code)) {
        if (selectedCustomProcedure?.codes.includes(code)) {
          return;
        }
        const newCdtCodes = currentValue.cdtCodes.filter((c) => c !== code);
        const allCodes = Array.from(new Set([...newCdtCodes, ...bundleCodes]));
        const { totalFee, totalDuration } = calculateTotals(newCdtCodes, selectedCustomProcedure ?? null);
        onChange?.({ ...currentValue, cdtCodes: newCdtCodes }, allCodes, getDescriptionsForCodes(allCodes), totalFee, totalDuration);
      } else {
        if (allSelectedCdtCodes.length >= MAX_CDT_CODES) {
          return;
        }
        const newCdtCodes = [...currentValue.cdtCodes, code];
        const allCodes = Array.from(new Set([...newCdtCodes, ...bundleCodes]));
        const { totalFee, totalDuration } = calculateTotals(newCdtCodes, selectedCustomProcedure ?? null);
        onChange?.({ ...currentValue, cdtCodes: newCdtCodes }, allCodes, getDescriptionsForCodes(allCodes), totalFee, totalDuration);
      }
    },
    [currentValue, onChange, allSelectedCdtCodes, selectedCustomProcedure, getDescriptionsForCodes, calculateTotals],
  );

  const canSelectCustomProcedure = useCallback(
    (procedure: CustomProcedure) => {
      if (selectedCustomProcedure && selectedCustomProcedure.id !== procedure.id) {
        return false;
      }
      const procedureCodes = new Set(procedure.codes);
      const additionalCodes = currentValue.cdtCodes.filter(
        (code) => !procedureCodes.has(code),
      );
      return procedureCodes.size + additionalCodes.length <= MAX_CDT_CODES;
    },
    [currentValue.cdtCodes, selectedCustomProcedure],
  );

  const isCdtCodeDisabled = useCallback(
    (code: string) => {
      if (selectedCustomProcedure?.codes.includes(code)) {
        return true;
      }
      if (allSelectedCdtCodes.length >= MAX_CDT_CODES && !allSelectedCdtCodes.includes(code)) {
        return true;
      }
      return false;
    },
    [selectedCustomProcedure, allSelectedCdtCodes],
  );

  const getDisplayValue = () => {
    if (allSelectedCdtCodes.length === 0) return placeholder || "";

    if (selectedCustomProcedure) {
      const bundleLabel = `${selectedCustomProcedure.codes.join(", ")} - ${selectedCustomProcedure.name}`;
      return bundleLabel;
    }

    return allSelectedCdtCodes.join(", ");
  };

  const renderChips = () => {
    if (allSelectedCdtCodes.length === 0) return null;

    return (
      <ChipsContainer sx={{ minWidth: 0 }}>
        {currentValue.cdtCodes.map((code) => (
          <StyledChip
            key={code}
            label={code}
            size="small"
            onDelete={(e) => handleRemoveChip(code, e)}
            deleteIcon={
              <Box component="span" sx={{ cursor: "pointer", display: "flex" }}>
                <CrossIcon width="12" height="12" />
              </Box>
            }
            onMouseDown={(e) => e.stopPropagation()}
          />
        ))}
        {selectedCustomProcedure && (
          <Tooltip
            title={`${selectedCustomProcedure.codes.join(", ")} - ${selectedCustomProcedure.name}`}
            placement="top"
            arrow
            enterDelay={300}
          >
            <StyledChip
              label={`${selectedCustomProcedure.codes.join(", ")} - ${selectedCustomProcedure.name}`}
              size="small"
              onDelete={handleRemoveCustomProcedure}
              deleteIcon={
                <Box component="span" sx={{ cursor: "pointer", display: "flex" }}>
                  <CrossIcon width="12" height="12" />
                </Box>
              }
              onMouseDown={(e) => e.stopPropagation()}
              sx={{
                backgroundColor: colors.background.lightBlue,
                border: `1px solid ${colors.primary.main}`,
                maxWidth: "220px",
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                },
              }}
            />
          </Tooltip>
        )}
      </ChipsContainer>
    );
  };

  return (
    <Box sx={{ width: fullWidth ? "100%" : width }}>
      {label && (
        <Label>
          {label}
          {required && (
            <Box component="span" sx={{ color: "inherit" }}>
              {" "}
              *
            </Box>
          )}
        </Label>
      )}
      <FormControl fullWidth sx={{ height: "100%", width: "100%" }}>
        <StyledSelect
          value={allSelectedCdtCodes}
          onChange={() => {}}
          disabled={disabled}
          multiple
          sx={{
            minWidth: 0,
            maxWidth: "100%",
            "& .MuiSelect-select": {
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              minWidth: 0,
            },
          }}
          displayEmpty
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          IconComponent={() => (
            <IconContainer>
              <BottomArrowIcon width="16px" height="16px" />
            </IconContainer>
          )}
          renderValue={() => (
            <ValueContainer sx={{ minWidth: 0, overflow: "hidden" }}>
              <Box sx={{ mr: 1, flexShrink: 0, display: "flex" }}>
                <SearchIcon width="16px" height="16px" />
              </Box>
              {renderChips() || (
                <Typography
                  sx={{
                    color:
                      allSelectedCdtCodes.length > 0
                        ? `${colors.text.primary}!important`
                        : `${colors.text.secondary}!important`,
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {getDisplayValue()}
                </Typography>
              )}
            </ValueContainer>
          )}
          name={name}
          id={id}
          onFocus={onFocus}
          onBlur={onBlur}
          MenuProps={{
            autoFocus: false,
            PaperProps: {
              sx: {
                ...menuPaperStyles,
                maxHeight: 350,
                minWidth: '800px!important',
                padding: '0 10px 10px 10px',
              },
            },
          }}
        >
          <SearchContainer>
            <Search
              placeholder={t(
                "editableStep.searchProceduresOrCodes",
                "Search procedures or codes...",
              )}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
            />
          </SearchContainer>

          {proceduresLoading ? (
            <MenuItem disabled>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  py: 1,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            </MenuItem>
          ) : (
            <>
              <SectionHeader>
                <SectionHeaderText>
                  {t("editableStep.customProcedures", "CUSTOM PROCEDURES")}
                </SectionHeaderText>
              </SectionHeader>

              {selectedCustomProcedure && (
                <ClearButton
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleRemoveCustomProcedure(e);
                  }}
                >
                  <ClearButtonText>
                    {t(
                      "editableStep.clearCustomProcedure",
                      "Clear custom procedure to select CDT codes",
                    )}
                  </ClearButtonText>
                </ClearButton>
              )}

              {filteredCustomProcedures.length === 0 ? (
                <MenuItem disabled>
                  <EmptyMessage>
                    {t(
                      "editableStep.noCustomProceduresFound",
                      "No custom procedures found",
                    )}
                  </EmptyMessage>
                </MenuItem>
              ) : (
                filteredCustomProcedures.map((procedure) => {
                  const isSelected = selectedCustomProcedure?.id === procedure.id;
                  const canSelect = canSelectCustomProcedure(procedure);

                  return (
                    <CustomProcedureItem
                      key={`custom-${procedure.id}`}
                      isSelected={isSelected}
                      canSelect={canSelect}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isSelected) {
                          handleRemoveCustomProcedure(e);
                        } else if (canSelect) {
                          handleCustomProcedureSelect(procedure);
                        }
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", flex: 1, gap: 1 }}>
                        <FileIcon width="16" height="16" fill={colors.text.secondary} />
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
                          <ItemLabel>{procedure.name}</ItemLabel>
                          {procedure.codes.length > 0 && (
                            <>
                              <ItemLabel sx={{ color: colors.text.secondary }}>{" -"}</ItemLabel>
                              <Typography sx={{ fontSize: "14px", color: colors.text.secondary }}>
                                {procedure.codes.join(", ")}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>
                      {isSelected && (
                        <Box
                          sx={{
                            ml: 2,
                            display: "flex",
                            width: "16px",
                            flexShrink: 0,
                          }}
                        >
                          <CheckIcon
                            fill={colors.status.activeText}
                            width="16"
                            height="16"
                          />
                        </Box>
                      )}
                    </CustomProcedureItem>
                  );
                })
              )}

              <SectionHeader
                sx={{
                  borderTop: `1px solid ${colors.border.light}`,
                  marginTop: "8px",
                }}
              >
                <SectionHeaderText>
                  {t("editableStep.cdtCodes", "CDT CODES")}
                </SectionHeaderText>
              </SectionHeader>

              {selectedCustomProcedure && (
                <ClearButton>
                  <ClearButtonText>
                    {t(
                      "editableStep.clearCustomProcedure",
                      "Clear custom procedure to select CDT codes",
                    )}
                  </ClearButtonText>
                </ClearButton>
              )}

              {filteredCdtCodes.length === 0 ? (
                <MenuItem disabled>
                  <EmptyMessage>
                    {t("editableStep.noCdtCodesFound", "No CDT codes found")}
                  </EmptyMessage>
                </MenuItem>
              ) : (
                filteredCdtCodes.map((code) => {
                  const isSelected = allSelectedCdtCodes.includes(code.code);
                  const isDisabled = isCdtCodeDisabled(code.code);
                  const isFromBundle = selectedCustomProcedure?.codes.includes(code.code);
                  const label = `${code.code} - ${code.description}`;

                  return (
                    <CdtCodeItem
                      key={code.code}
                      isSelected={isSelected}
                      isDisabled={isDisabled}
                      isFromBundle={isFromBundle}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isDisabled) {
                          handleCdtCodeSelect(code.code);
                        }
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                        <Checkbox
                          checked={isSelected}
                          disabled={isDisabled}
                          sx={{ mr: 1, p: 0 }}
                        />
                        <ItemLabel isFromBundle={isFromBundle}>{label}</ItemLabel>
                      </Box>
                    </CdtCodeItem>
                  );
                })
              )}
            </>
          )}
        </StyledSelect>
      </FormControl>

      {helperText && (
        <Typography
          variant="caption"
          sx={{
            color: error ? "status.error" : "text.secondary",
            fontSize: "12px",
            mt: 0.5,
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default CDTAndCustomProcedureSelect;
